/**
 * V5 — Structured edit plan before %%REPO_EDIT%% is allowed.
 */

import type { RepoContextBuildResult } from '@/services/githubRepoContextRouter';
import { scorePathRelevance } from '@/services/githubRepoContextRouter';

export interface EditPlan {
  summary: string;
  targetFiles: string[];
  relatedFiles: string[];
  requiredContext: string[];
  riskLevel: 'low' | 'medium' | 'high';
  needsClarification: boolean;
  clarificationReason?: string;
}

function resolvePrimaryTargetFiles(
  message: string,
  contextResult: RepoContextBuildResult
): string[] {
  if (contextResult.primaryIntentPath) {
    return [contextResult.primaryIntentPath];
  }

  const nonGraph = contextResult.candidates.filter(
    c => c.source !== 'graph' && contextResult.resolvedPaths.includes(c.path)
  );
  if (nonGraph.length === 0) {
    return contextResult.resolvedPaths.slice(0, 1);
  }

  const ranked = nonGraph
    .map(c => ({
      path: c.path,
      score: c.confidence * 0.5 + scorePathRelevance(message, c.path) * 0.5,
    }))
    .sort((a, b) => b.score - a.score);

  return ranked[0] ? [ranked[0].path] : contextResult.resolvedPaths.slice(0, 1);
}

export function buildEditPlan(opts: {
  message: string;
  contextResult: RepoContextBuildResult;
  graphNeighbors?: string[];
}): EditPlan {
  const { message, contextResult, graphNeighbors = contextResult.graphExpandedPaths } = opts;
  const fullFilePaths = new Set(contextResult.fullFiles.map(f => f.path));
  const targetFiles = resolvePrimaryTargetFiles(message, contextResult);
  const relatedFiles = [...new Set(graphNeighbors.filter(p => !targetFiles.includes(p)))];

  const missingFullFile = targetFiles.filter(p => !fullFilePaths.has(p));
  const requiredContext = [...targetFiles];

  if (targetFiles.length === 0) {
    return {
      summary: 'Edit intent detected but no target file resolved.',
      targetFiles: [],
      relatedFiles,
      requiredContext: [],
      riskLevel: 'high',
      needsClarification: true,
      clarificationReason: 'No target file could be resolved from the message.',
    };
  }

  if (missingFullFile.length > 0) {
    return {
      summary: `Edit requested but full content missing for: ${missingFullFile.join(', ')}`,
      targetFiles,
      relatedFiles,
      requiredContext,
      riskLevel: 'high',
      needsClarification: true,
      clarificationReason: 'Target file could not be fully loaded from GitHub or repo index.',
    };
  }

  if (targetFiles.length === 1 && fullFilePaths.has(targetFiles[0])) {
    return {
      summary: `Single-file edit: ${targetFiles[0]}`,
      targetFiles,
      relatedFiles,
      requiredContext,
      riskLevel: 'low',
      needsClarification: false,
    };
  }

  const allFetched = targetFiles.every(p => fullFilePaths.has(p));
  if (allFetched) {
    return {
      summary: `Multi-file edit across ${targetFiles.length} files`,
      targetFiles,
      relatedFiles,
      requiredContext,
      riskLevel: 'medium',
      needsClarification: false,
    };
  }

  return {
    summary: `Partial context for edit: missing ${missingFullFile.join(', ')}`,
    targetFiles,
    relatedFiles,
    requiredContext,
    riskLevel: 'high',
    needsClarification: true,
    clarificationReason: 'Not all target files were fully loaded.',
  };
}

export function isEditPlanAllowed(plan: EditPlan, fullFilePaths: string[]): boolean {
  if (plan.needsClarification || plan.riskLevel === 'high') return false;
  const loaded = new Set(fullFilePaths);
  return plan.requiredContext.every(p => loaded.has(p));
}

export function formatEditPlanBlock(plan: EditPlan): string {
  return `
=== EDIT PLAN (required before changes) ===
Target files: ${plan.targetFiles.length ? plan.targetFiles.join(', ') : '(none)'}
Related files: ${plan.relatedFiles.length ? plan.relatedFiles.join(', ') : '(none)'}
Risk: ${plan.riskLevel}
${plan.needsClarification ? `Clarification needed: ${plan.clarificationReason ?? plan.summary}` : plan.summary}
`.trim();
}
